/**
 * The one place that answers "what identity has this person already proven?"
 *
 * This did not exist, and its absence is why the app asks for the same national
 * ID more than once. What existed instead:
 *
 *   - `verificationService.getOverview` selects status, provenance and scores
 *     and deliberately not `document_number`, so no screen could read it back.
 *   - `amlComplianceService.getIdentityPrefill` resolved name, date of birth,
 *     gender and nationality with provenance — the right idea, private to one
 *     screen, and it never resolved the number either.
 *   - `app/(tabs)/profile/verification/license.tsx` gave up and inlined
 *     `supabase.from('identity_documents').select('document_number')` inside the
 *     component, reaching around the service layer. That is not a shortcut, it
 *     is the evidence: the need was real and unmet.
 *
 * So the bank-account form asks a host to type a DUI that Verifik already
 * checked against the national registry, and throws it away on the server.
 *
 * Everything here is a READ. Nothing in this file writes to identity_documents:
 * that column has one owner, the verify-* edge functions, behind a database
 * trigger that normalises it (20260809161000).
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import type { Json } from '../types/db-rows'

/** @public */
export interface VerifiedIdentity {
  /** Canonical storage form. `null` when the document was approved without one. */
  documentNumber: string | null
  documentType: string
  issuingCountry: string
  firstName: string | null
  lastName: string | null
  /** Convenience: the name as the registry returned it, already joined. */
  fullName: string | null
  dateOfBirth: string | null
  verifiedAt: string | null
  provider: string | null
  /**
   * The provider's own extraction. Part of a verified identity, not a
   * separate concern: sex and nationality live only here, and making callers
   * fetch it themselves is how a second copy of this query gets written.
   */
  extractedData: Json | null
}

const VERIFIED_IDENTITY_COLUMNS =
  'document_number, document_type, issuing_country, first_name, last_name, date_of_birth, ' +
  'status, canonical_status, verified_at, verification_provider, extracted_data'

interface VerifiedIdentityRow {
  document_number: string | null
  document_type: string | null
  issuing_country: string | null
  first_name: string | null
  last_name: string | null
  date_of_birth: string | null
  status: string | null
  canonical_status: string | null
  verified_at: string | null
  verification_provider: string | null
  extracted_data: Json | null
}

/**
 * A row counts as proven only if a provider approved it.
 *
 * `canonical_status` is the post-decision source of truth and `status` is the
 * legacy mirror; a row written before the dual-write only has the latter, which
 * is why both are consulted. `verified_at` alone is NOT enough — a superseded
 * row keeps its timestamp, and after 20260809162000 a duplicate DUI is resolved
 * by moving the losing row to 'expired' while it keeps both its number and its
 * verified_at. Trusting the timestamp would hand the loser back the identity.
 */
function isProven(row: VerifiedIdentityRow): boolean {
  return row.canonical_status === 'approved' || row.status === 'approved'
}

/** One mapping, used by every read below. */
function toVerifiedIdentity(row: VerifiedIdentityRow): VerifiedIdentity {
  const fullName = [row.first_name, row.last_name].filter(Boolean).join(' ').trim()
  return {
    documentNumber: row.document_number,
    documentType: row.document_type ?? 'dui',
    issuingCountry: row.issuing_country ?? 'SV',
    firstName: row.first_name,
    lastName: row.last_name,
    fullName: fullName || null,
    dateOfBirth: row.date_of_birth,
    verifiedAt: row.verified_at,
    provider: row.verification_provider,
    extractedData: row.extracted_data,
  }
}

class IdentityServiceImpl {
  /**
   * The identity this person has proven, or null if they have not proven one.
   *
   * @public
   */
  async getVerifiedIdentity(profileId: string): Promise<VerifiedIdentity | null> {
    if (!profileId) return null

    const { data, error } = await supabase
      .from('identity_documents')
      .select(VERIFIED_IDENTITY_COLUMNS)
      .eq('profile_id', profileId)
      .order('verified_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(10)

    if (error) throw ServiceError.fromSupabaseError(error)

    const proven = ((data ?? []) as unknown as VerifiedIdentityRow[]).find(isProven)
    if (!proven) return null

    return toVerifiedIdentity(proven)
  }

  /**
   * The most recent attempt, whatever came of it.
   *
   * A different question from `getVerifiedIdentity`, and the difference is the
   * point: after a failed verification there IS no verified identity, and that
   * is exactly when the screen needs to remember what the person typed. Making
   * somebody retype a nine-digit document number to correct a blurry photo is
   * the clearest possible way to lose them.
   *
   * @public
   */
  async getLatestAttempt(profileId: string): Promise<VerifiedIdentity | null> {
    if (!profileId) return null

    const { data, error } = await supabase
      .from('identity_documents')
      .select(VERIFIED_IDENTITY_COLUMNS)
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) return null
    return toVerifiedIdentity(data as unknown as VerifiedIdentityRow)
  }
}

export const identityService = new IdentityServiceImpl()
